// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie } from "@insite/client-framework/Common/Cookies";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { CartModel } from "@insite/client-framework/Types/ApiModels";

export const getDataLayer = () => {
    return (window as any).dataLayer;
};

export const getBillToCustomerNumber = (state: ApplicationState) => {
    const billToState = getBillToState(state, state.context.session.billToId);
    return billToState?.value?.customerNumber;
};

export const trackAsImpersonation = (state: ApplicationState) => {
    // XSD-15252 tie these scenarioes with the impersonation flag sent in ga4 events
    const session = getSession(state);
    const isInternalCustomerServiceUser = session.properties?.isInternalCustomerServiceUser?.toLowerCase() === "true";
    return session.isSalesPerson || isInternalCustomerServiceUser || !!getCookie("ImpersonateBy");
};

export const CHECKOUT_STEPS = {
    CART: 0,
    ADDRESS: 1,
    SHIPPING: 2,
    PAYMENT: 3,
    REVIEW: 4,
};

// All GA4 events starts with "trackGa"

// Checkout Events
/* eslint-disable */
(function (window) {
    (window as any).trackGaCheckoutFunnel = (
        step: number,
        cart: CartModel,
        discount?: number,
        includeBrand?: boolean,
        includeCategory?: boolean,
    ) => {
        const dataLayer = getDataLayer();
        if (!dataLayer) {
            return;
        }

        const googleAnalyticsData = {
            ...(step === CHECKOUT_STEPS.ADDRESS && {
                event: "begin_checkout",
            }),
            ...(step === CHECKOUT_STEPS.SHIPPING && {
                event: "add_shipping_info",
                shipping_tier: cart.carrier?.description || "",
            }),
            ...(step === CHECKOUT_STEPS.ADDRESS && {
                event: "add_payment_info",
                payment_type: cart.paymentMethod?.description || "",
            }),
            items: cart?.cartLines?.map(cartLine => ({
                item_name: cartLine.shortDescription,
                item_id: cartLine.erpNumber,
                price: cartLine.pricing?.unitNetPrice,
                quantity: cartLine.qtyOrdered,
                item_brand: includeBrand ? cartLine.brand?.name || "" : undefined,
                item_category: includeCategory ? cartLine.properties?.["category"] || "" : undefined,
                item_list_id: includeCategory ? cartLine.properties?.["categoryId"] || "" : undefined,
            })),
            discount: discount || 0,
            value: cart.orderSubTotal,
        };

        dataLayer.push(googleAnalyticsData);
    };
})(typeof window != "undefined" ? window : {});
/* eslint-enable */

// Product Categories Widget Event
export const trackGaProductCategoriesWidgetEvent = (clickUrl: string, impersonation: boolean) => {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const googleAnalyticsData = {
        event: "product_categories_widget",
        destination: clickUrl,
        impersonation,
    };
    dataLayer.push(googleAnalyticsData);
};

// Podcast Event
export const trackGaPodcastEvent = (clickUrl: string, impersonation: boolean, customerNumber?: string) => {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const googleAnalyticsData = {
        event: "podcast_click",
        destination: clickUrl,
        ...(customerNumber && { affiliation: customerNumber }),
        impersonation,
    };

    dataLayer.push(googleAnalyticsData);
};

// MyAccountFlyout Event
export const trackGaMyAccountFlyOutEvent = (
    clickUrl: string,
    clickText: string,
    impersonation: boolean,
    customerNumber?: string,
) => {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const googleAnalyticsData = {
        event: "my_acct_flyout",
        text: clickText,
        destination: clickUrl,
        affiliation: customerNumber,
        impersonation,
    };

    dataLayer.push(googleAnalyticsData);
};

// Make Dashboard My Homepage Event
export const trackGaMakeDashboardMyHomePageEvent = (customerNumber: string, impersonation: boolean) => {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const googleAnalyticsData = {
        event: "make_dash_homepg",
        affiliation: customerNumber,
        impersonation,
    };

    dataLayer.push(googleAnalyticsData);
};

// Quick Product Lookup Event
export const trackGaQuickProductLookupEvent = (searchTerm: string, impersonation: boolean, customerNumber?: string) => {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const googleAnalyticsData = {
        event: "quick_product_lookup",
        term: searchTerm?.toLowerCase(),
        affiliation: customerNumber,
        impersonation,
    };

    dataLayer.push(googleAnalyticsData);
};

// Login Event
export const trackGaLoginEvent = (customerNumber: string, impersonation: boolean) => {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const googleAnalyticsData = {
        event: "login",
        affiliation: customerNumber,
        impersonation,
    };

    dataLayer.push(googleAnalyticsData);
};

// Toolbar Nav Click Event
export const trackGaToolbarNavClickEvent = (label: string, impersonation: boolean) => {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const googleAnalyticsData = {
        event: "tool_bar_nav",
        text: label,
        impersonation,
    };

    dataLayer.push(googleAnalyticsData);
};

// Navigation Bar Click Event
export const trackGaNavBarClickEvent = (label: string, impersonation: boolean) => {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const googleAnalyticsData = {
        event: "nav_bar",
        text: label,
        impersonation,
    };

    dataLayer.push(googleAnalyticsData);
};

// Product Nav Event
export const trackGaProductNavButtonEvent = (impersonation: boolean, clickUrl?: string) => {
    const dataLayer = getDataLayer();
    if (!dataLayer || !clickUrl) {
        return;
    }

    const googleAnalyticsData = {
        event: "product_nav_button",
        destination: clickUrl,
        impersonation,
    };

    dataLayer.push(googleAnalyticsData);
};

// Resource Center Manual Click Event
export const trackGaResourceManualEvent = (
    clickText: string,
    impersonation: boolean,
    userEmail?: string,
    customerNumber?: string,
) => {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const googleAnalyticsData = {
        event: "manuals_click",
        text: clickText,
        user: userEmail || "",
        affiliation: customerNumber || "",
        impersonation,
    };

    dataLayer.push(googleAnalyticsData);
};

// PDP Product Resources Click Event
export const trackGaPdpProductResourcesEvent = (
    data: { clickText: string; clickUrl: string; productNumber: string },
    impersonation: boolean,
    userEmail?: string,
    customerNumber?: string,
) => {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const googleAnalyticsData = {
        event: "pdp_prod_resources",
        text: data.clickText,
        destination: data.clickUrl,
        user: userEmail || "",
        affiliation: customerNumber || "",
        item_id: data.productNumber,
        impersonation,
    };

    dataLayer.push(googleAnalyticsData);
};

// Search Event
export const trackGaSearchEvent = (
    searchTerm: string,
    searchResultCount: number,
    impersonation: boolean,
    customerNumber?: string,
) => {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const googleAnalyticsData = {
        event: "search",
        search_term: searchTerm?.toLowerCase(),
        numSearchResults: searchResultCount,
        affiliation: customerNumber || "",
        impersonation,
    };

    dataLayer.push(googleAnalyticsData);
};

// My Account Pages Navigation Event
export const trackGaMyAccountPagesNavEvent = (
    event: string | null,
    clickUrl: string,
    clickText: string,
    impersonation: boolean,
    customerNumber?: string,
    myAccountPagesLinks?: {
        orderHistoryPageLink?: string;
        invoiceHistoryPageLink?: string;
        myQuotesPageLink?: string;
        myProfilePageLink?: string;
        userAdministrationPageLink?: string;
        sentinelPageLink?: string;
        courseManagementPageLink?: string;
        myCoursesPageLink?: string;
    },
) => {
    const dataLayer = getDataLayer();
    if (!dataLayer || !clickUrl || (!event && !myAccountPagesLinks)) {
        return;
    }

    let eventName = event;
    if (!eventName && myAccountPagesLinks) {
        const {
            orderHistoryPageLink,
            invoiceHistoryPageLink,
            myQuotesPageLink,
            myProfilePageLink,
            userAdministrationPageLink,
            sentinelPageLink,
            courseManagementPageLink,
            myCoursesPageLink,
        } = myAccountPagesLinks;

        switch (clickUrl) {
            case orderHistoryPageLink:
                eventName = "myacct_orderhistory";
                break;
            case invoiceHistoryPageLink:
                eventName = "myacct_invoicehistory";
                break;
            case myQuotesPageLink:
                eventName = "myacct_myquotes";
                break;
            case myProfilePageLink:
                eventName = "myacct_myprofile";
                break;
            case userAdministrationPageLink:
                eventName = "myacct_useradmin";
                break;
            case sentinelPageLink:
                eventName = "myacct_sentinellink";
                break;
            case courseManagementPageLink:
                eventName = "myacct_coursemanagement";
                break;
            case myCoursesPageLink:
                eventName = "myacct_mycourses";
                break;
        }
    }

    if (!eventName) {
        return;
    }

    const googleAnalyticsData = {
        event: eventName,
        text: clickText,
        destination: clickUrl,
        affiliation: customerNumber || "",
        impersonation,
    };

    dataLayer.push(googleAnalyticsData);
};

// Button Click Event
export const trackGaButtonClickEvent = (
    event: string,
    clickText: string,
    impersonation: boolean,
    customerNumber?: string,
    clickUrl?: string,
    username?: string,
) => {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const googleAnalyticsData = {
        event,
        text: clickText,
        ...(clickUrl && { destination: clickUrl }),
        ...(customerNumber && { affiliation: customerNumber }),
        ...(username && { username }),
        impersonation,
    };

    dataLayer.push(googleAnalyticsData);
};

// Resource Center Search Event
export const trackGaResourceCenterSearchEvent = (
    searchTerm: string,
    numSearchResults: number,
    impersonation: boolean,
    customerNumber?: string,
) => {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const googleAnalyticsData = {
        event: "resourcecenter_search",
        searchTerm: searchTerm?.toLowerCase(),
        numSearchResults,
        affiliation: customerNumber || "",
        impersonation,
    };

    dataLayer.push(googleAnalyticsData);
};

// Contact Us Event
export const trackGaContactUsEvent = (email: string, impersonation: boolean, customerNumber?: string) => {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const googleAnalyticsData = {
        event: "contactus",
        user: email,
        affiliation: customerNumber || "",
        impersonation,
    };

    dataLayer.push(googleAnalyticsData);
};

// Request to Quote Event
export const trackGaRequestQuoteEvent = (
    data: { productTitle: string; productNumber: string },
    email: string,
    impersonation: boolean,
    customerNumber?: string,
) => {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const googleAnalyticsData = {
        event: "request_for_quote",
        item: data.productTitle,
        user: email,
        affiliation: customerNumber || "",
        item_id: data.productNumber,
        impersonation,
    };

    dataLayer.push(googleAnalyticsData);
};

// Request to Rent Event
export const trackGaRequestRentEvent = (
    data: { productTitle: string; productNumber: string },
    email: string,
    impersonation: boolean,
    customerNumber?: string,
) => {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const googleAnalyticsData = {
        event: "request_to_rent",
        item: data.productTitle,
        user: email,
        affiliation: customerNumber || "",
        item_id: data.productNumber,
        impersonation,
    };

    dataLayer.push(googleAnalyticsData);
};

// NewsLetter Subscription Event
export const trackGaNewsSubscriptionEvent = (email: string, impersonation: boolean, customerNumber?: string) => {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const googleAnalyticsData = {
        event: "newsletter_subscribe",
        user: email,
        affiliation: customerNumber || "",
        impersonation,
    };

    dataLayer.push(googleAnalyticsData);
};

// Book Consultation Event
export const trackGaBookConsultationEvent = (email: string, impersonation: boolean, customerNumber?: string) => {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const googleAnalyticsData = {
        event: "book_consultation",
        user: email,
        affiliation: customerNumber || "",
        impersonation,
    };

    dataLayer.push(googleAnalyticsData);
};

// Search Dealer Event
export const trackGaSearchDealerEvent = (
    email: string,
    dealer: string,
    impersonation: boolean,
    customerNumber?: string,
) => {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const googleAnalyticsData = {
        event: "locate_a_dealer",
        user: email,
        dealer,
        affiliation: customerNumber || "",
        impersonation,
    };

    dataLayer.push(googleAnalyticsData);
};

// Shipment Details Event
export const trackGaShipmentDetailsEvent = (
    clickText: string,
    impersonation: boolean,
    customerNumber?: string,
    clickUrl?: string,
) => {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const googleAnalyticsData = {
        event: "myacct_shipmentdetails",
        text: clickText,
        destination: clickUrl,
        affiliation: customerNumber,
        impersonation,
    };

    dataLayer.push(googleAnalyticsData);
};

// PDP Copy Link Event
export const trackGaPdpCopyEvent = (
    data: { productTitle: string; productNumber: string },
    impersonation: boolean,
    email?: string,
    customerNumber?: string,
) => {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const googleAnalyticsData = {
        event: "pdp_copylink",
        item: data.productTitle,
        user: email,
        affiliation: customerNumber || "",
        item_id: data.productNumber,
        impersonation,
    };

    dataLayer.push(googleAnalyticsData);
};

// Related Products Event
export const trackGaRelatedProductsEvent = (
    productNumber: string,
    relatedProductNumber: string,
    impersonation: boolean,
    customerNumber?: string,
) => {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const googleAnalyticsData = {
        event: "related_products",
        item_id: productNumber,
        destination: relatedProductNumber,
        affiliation: customerNumber || "",
        impersonation,
    };

    dataLayer.push(googleAnalyticsData);
};

// BOM CTA Event
export const trackGaBomCtaEvent = (productNumber: string, impersonation: boolean, customerNumber?: string) => {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const googleAnalyticsData = {
        event: "bom_cta",
        item_id: productNumber,
        affiliation: customerNumber || "",
        impersonation,
    };

    dataLayer.push(googleAnalyticsData);
};

// BOM Number Event
export const trackGaBomNumberEvent = (productNumber: string, impersonation: boolean, customerNumber?: string) => {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const googleAnalyticsData = {
        event: "bom_number",
        item_id: productNumber,
        affiliation: customerNumber || "",
        impersonation,
    };

    dataLayer.push(googleAnalyticsData);
};

// BOM Kit Filter Event
export const trackGaKitFilterEvent = (
    productNumber: string,
    kitType: string,
    impersonation: boolean,
    customerNumber?: string,
) => {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const googleAnalyticsData = {
        event: "kit_filter",
        item_id: productNumber,
        kitType,
        affiliation: customerNumber || "",
        impersonation,
    };

    dataLayer.push(googleAnalyticsData);
};

// RFQ/Configure product button click Event
export const trackGaRfqConfigureButtonClickEvent = (
    productNumber: string,
    ctaText: string,
    impersonation: boolean,
    customerNumber?: string,
) => {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const googleAnalyticsData = {
        event: "bom_rfq_configure_button",
        item_id: productNumber,
        text: ctaText,
        affiliation: customerNumber || "",
        impersonation,
    };

    dataLayer.push(googleAnalyticsData);
};

// Request for quote CTA in footer Event
export const trackGaRfqFooterEvent = (
    productNumber: string,
    ctaText: string,
    impersonation: boolean,
    customerNumber?: string,
) => {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const googleAnalyticsData = {
        event: "bom_rfq_configure_button_footer",
        item_id: productNumber,
        text: ctaText,
        affiliation: customerNumber || "",
        impersonation,
    };

    dataLayer.push(googleAnalyticsData);
};
